import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { searchPersons } from "../../features/persons/service/personService";
import { Person as DirectoryPerson } from "../../features/persons/types";
import { SharedMUIList } from "../common/shared/components/SharedMUIList";
import { RELATIONS } from "../common/shared/types";

const ALL_RELATIONS: string[] = Object.values(RELATIONS);

// "Father"/"Mother" imply the assigned person IS the student's real father/mother record — only
// true when assigned through the dedicated Father/Mother tabs (which use fixedRelation, not this
// list). Excluding them here stops a search-assigned, unrelated person from being labeled Father/Mother.
export const NON_PARENT_RELATIONS: string[] = ALL_RELATIONS.filter(
  (relation) => relation !== RELATIONS.Father && relation !== RELATIONS.Mother
);

type PersonAssignSearchDialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  onSave: (person: DirectoryPerson, relation: string) => Promise<void>;
  // When set (e.g. "Father"/"Mother"), the relation is implied — skip the Relation select entirely.
  fixedRelation?: string;
  // Overrides the relation options shown (e.g. to add "Guardian" for the Emergency dialog).
  // Defaults to every relation except Father/Mother (see NON_PARENT_RELATIONS above).
  relationOptions?: string[];
};

export default function PersonAssignSearchDialog({
  open,
  title,
  onClose,
  onSave,
  fixedRelation,
  relationOptions = NON_PARENT_RELATIONS,
}: PersonAssignSearchDialogProps) {
  const [name, setName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [results, setResults] = useState<DirectoryPerson[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<DirectoryPerson | null>(null);
  const [relation, setRelation] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setMobileNo("");
    setResults(null);
    setSelectedPerson(null);
    setRelation("");
    setSaveError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSearch = async () => {
    setSearching(true);
    try {
      const page = await searchPersons(name.trim(), mobileNo.trim());
      setResults(page.content);
    } finally {
      setSearching(false);
    }
  };

  const effectiveRelation = fixedRelation ?? relation;

  // Father/Mother are assigned directly (fixedRelation set) and don't require a mobile number here.
  // Guardian/Emergency contacts do, since the school relies on that number to reach them.
  const mobileMissing = !selectedPerson?.mobileNo || String(selectedPerson.mobileNo).trim().length === 0;
  const blockedByMissingMobile = !fixedRelation && mobileMissing;

  const handleSave = async () => {
    if (!selectedPerson || !effectiveRelation) return;
    if (blockedByMissingMobile) {
      setSaveError("This person has no mobile number on file. A guardian or emergency contact must have a valid mobile number.");
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      await onSave(selectedPerson, effectiveRelation);
      handleClose();
    } catch (err) {
      // Save failure is surfaced by the caller (SharedAlert); keep the dialog open so they can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>{title}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {!selectedPerson ? (
          <>
            <TextField label="Name" fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} />
            <TextField label="Mobile No" fullWidth size="small" value={mobileNo} onChange={(e) => setMobileNo(e.target.value)} />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSearch}
              disabled={(!name.trim() && !mobileNo.trim()) || searching}
            >
              {searching ? "Searching..." : "Search"}
            </Button>

            {results && results.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No matches found.
              </Typography>
            )}

            {results && results.length > 0 && (
              <List dense sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                {results.map((person) => (
                  <ListItemButton key={person.externalId} onClick={() => setSelectedPerson(person)}>
                    <ListItemText primary={person.name} secondary={person.mobileNo || "No mobile on file"} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: "pointer", fontWeight: 500 }}
              onClick={() => {
                setSelectedPerson(null);
                setSaveError(null);
              }}
            >
              ← Back to results
            </Typography>

            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {selectedPerson.name}
              </Typography>
              <Typography variant="body2">
                <strong>Mobile No:</strong> {selectedPerson.mobileNo || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedPerson.email || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Date Of Birth:</strong> {selectedPerson.dob || "N/A"}
              </Typography>
            </Box>

            {!fixedRelation && <SharedMUIList lable="Relation" value={relation} list={relationOptions} onChange={setRelation} />}

            {saveError && (
              <Typography variant="body2" color="error">
                {saveError}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        {selectedPerson && (
          <Button variant="contained" onClick={handleSave} disabled={!effectiveRelation || saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
